// Dip — homepage interactivity.
//   <dip-tabs>         accessible product tab bars (testimonials + how-it-works)
//   <dip-quotes>       the buy-column testimonial carousel (snippets/dip-buy-quotes.liquid),
//                      same behaviour DipBuyBox runs inside the PDP buy boxes
//   <dip-home-bundle>  the whole-home bundle configurator: 3 tiers, deselectable
//                      lines, subscribe/one-time, welcome-kit gating + cart add.
//   reveal observer    same additive .dip-reveal entrance dip-buy-box.js runs on
//                      the PDPs (buy-box doesn't load on the homepage).
//
// Cart mechanics mirror assets/dip-buy-box.js exactly (the verified flow):
// items[] posted to Theme.routes.cart_add_url with native selling_plan ids;
// bottle + cloth ride as one-time lines zeroed by the automatic BXGY discount;
// the MSC gift subscription's first cycle is zeroed by the guard-managed code
// (dip-msc-guard.js, loaded on every page) via the localStorage offer registry.
// PAID-KIT MODE (2026-07-19): with no laundry line (or one-time), the kit is an
// optional paid add-on. The same lines are posted at full price; the guard applies
// the per-size MSC-KIT-* code BXGY (from the msc_kit_codes setting) to zero bottle
// + cloth against the MSC pack, so the kit bills at the MSC pack price only (the
// MSC Starter offer). FREE-KIT-MSC-1 is EXPIRED and must stay expired.

import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { initGallery } from '@theme/dip-gallery';
// <dip-tabs> and <dip-quotes> are shared with the PDPs; one definition each, no load-order race.
import '@theme/dip-tabs';
import '@theme/dip-quotes';
import { initStickyBar } from '@theme/dip-sticky-bar';


/**
 * Read the basket's item count out of rendered section HTML.
 *
 * snippets/cart-bubble.liquid prints cart.item_count into [data-testid="cart-bubble"],
 * and every add already asks the Section Rendering API for the header, so the count
 * arrives in the SAME response as the drawer markup. Using it means an add is one
 * round trip: the drawer can open immediately instead of waiting on a second
 * GET /cart.js (which read as a lag, then a snap).
 *
 * Duplicated byte-identically in dip-buy-box.js, dip-home.js and dip-cart.js, the
 * same convention the .dip-reveal observer uses. Keep the three copies in sync.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

/**
 * One welcome kit per basket. Kit lines (bottle, cloth, the gift pack) are marked
 * `_kit: 1` by #lineItems; any that already sit in the cart are dropped from the POST,
 * and the marker is stripped before the request either way. Without this, a second
 * bundle add doubles the hardware and the discounts come apart at checkout: the BXGY
 * frees an exact quantity, and Shopify allocates it to the CHEAPEST eligible units, so
 * with 2 bottles + 2 cloths in the cart both cloths go free and both £15 bottles BILL,
 * while the Basic gift code (FREE-MSC-LD*) zeroes the WHOLE pack line, gifting a second
 * pack that was never offered (Charlie's store review 2, item 10).
 * Duplicated byte-identically in dip-buy-box.js and dip-home.js; keep the copies in sync.
 *
 * @param {Array<{id: number, quantity: number, _kit?: number}>} items
 * @returns {Promise<Array<{id: number, quantity: number}>>}
 */
const dipDropKitDupes = async (items) => {
  if (items.some((it) => it._kit)) {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const inCart = new Set((cart.items || []).map((it) => Number(it.variant_id)));
      items = items.filter((it) => !(it._kit && inCart.has(Number(it.id))));
    } catch { /* offline or blocked: send as built rather than lose the sale */ }
  }
  return items.map(({ _kit, ...line }) => line);
};

// Active presentment currency + locale, injected by Shopify on the storefront. Liquid has already
// converted every price in the data island to this currency, so JS only formats — it never converts.
const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
// Always 100: Shopify stores prices as amount x 100 in every currency, zero-decimal ones included
// (JPY 1,000 = 100000). Deriving this from the currency's fraction digits inflates those 100x.
const MINOR = 100;
const money = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);
const _fmt0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyTidy = (minorUnits) => ((minorUnits || 0) % MINOR === 0 ? _fmt0 : _fmt).format((minorUnits || 0) / MINOR);
const perMonth = (raw, months) => money(raw / (months || 1));

// ---- Tabs -------------------------------------------------------------------
// Canonical PDP tab pattern (matches dip-buy-box.js #initTabsets): a
// .dip-tabbar[role=tablist] of .dip-tabbtn[role=tab] pills (charcoal fill when
// .is-on) + .dip-tabpanel[data-panel] panels toggled with is-hidden + hidden.
// No-JS: every panel visible; connect hides all but the selected one.

// ---- Buy-column testimonial carousel ----------------------------------------
// The PDP buy box drives its own copy of this from DipBuyBox (#initQuotes); this is the
// standalone element for every other surface that renders snippets/dip-buy-quotes.liquid.
// Same behaviour, same markup contract: .dip-quotes-track > .dip-quotes-slide+, with
// [ref=quotesDots|quotesPrev|quotesNext] optional. Grid-stack crossfade, one visible.
// Auto-rotates only while on screen; pauses on hover/focus; stops for good once the user
// drives it (arrow or swipe) so their chosen quote stays put.

// ---- Whole-home bundle (Sult-style kit builder) -------------------------------
// Three toggleable product image cards (Laundry, Dishwasher, Multi-Surface) + tier
// presets (Light Use / Most Popular / Big Family / Stock Up) that set each product's
// size + free-MSC pack + default cadence. Selecting Laundry auto-selects the kit +
// FREE flag. PDP parity: Trustpilot line, compact laundry scent pills, an "In your
// plan" recap with per-line +/- to add more, a PDP-styled price block, and the
// order-summary + subscription-info modals. Kit = 1 bottle + 1 cloth + MSC sheets.
class DipHomeBundle extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dip-data]');
    if (!dataEl) return;
    try {
      this.model = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[dip-home-bundle] bad data island', e);
      return;
    }

    this.threshold = Number(this.dataset.threshold) || 60;
    this.copy = this.model.copy || {};
    this.tiers = this.model.tiers || [];
    this.scentColours = this.model.scentColours || {};
    this.CHECK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const k = el.getAttribute('ref');
      if (!(k in this.refs)) this.refs[k] = el;
    });

    // First-pack-free codes, one per MSC pack size. Each is a Basic 100%-off code restricted to that
    // variant, so a size with no code CANNOT be given away — the kit shows it as a paid pack instead
    // of claiming a free one the cart would then bill for.
    let codes = {};
    try { codes = JSON.parse(this.dataset.mscCodes || '{}') || {}; } catch { codes = {}; }
    if (!Object.keys(codes).length && this.dataset.mscCode) {
      codes = { [this.model.msc?.freeSize || '1 pack']: this.dataset.mscCode };
    }
    this.mscCodes = codes;

    let di = this.tiers.findIndex((t) => t.isDefault);
    if (di < 0) di = Math.min(1, this.tiers.length - 1);
    this.tierIdx = Math.max(0, di);
    // The pre-selected tier is the fixed yardstick for the "save more / bigger kit" chips. Comparing
    // against the CURRENT tier instead would make every chip rewrite itself on each click.
    this.baseIdx = this.tierIdx;
    this.months = Number(this.#tier().months) || Number(this.model.defaultMonths) || 2;
    this.freqTouched = false;
    this.on = { laundry: true, dishwasher: true };
    // Each line owns its quantity. The fabric conditioner is deliberately NOT tied to the
    // laundry quantity: conditioner use is not 1:1 with detergent for most households, and
    // auto-doubling a ticked cross-sell is a £15+ surprise nobody asked for.
    this.qty = { laundry: 1, dishwasher: 1, fabcon: 1 };
    this.kitPick = { free: true, paid: false };
    // The sheet subscription inside the kit is separately droppable (PDP parity): the bottle and
    // cloth are one-time gifts, the sheets renew, and some people only want the hardware.
    this.msc = true;
    // Fabric conditioner add-on: pre-ticked (laundry PDP parity), deselectable.
    this.fab = !!this.model.fabcon;
    this.scent = this.model.laundry?.scents?.[0] || '';
    this.dwScent = this.model.dishwasher?.scents?.[0] || '';

    this.#bind();
    this.#publishGuardOffer();
    this.#initKitFlip();
    this.#initHowLink();
    this.#initSummary();
    // Floating buy bar, same component the PDPs use. Rendered only where the section setting asks for
    // it: the bundle PAGE wants one, the homepage does not (the builder is one section of seventeen
    // there, and a bar trailing the reader through the rest of the page is a nag, not a nudge).
    // Anchor on the CTA, not the gallery, same reasoning as dip-buy-box.js #initSticky:
    // the bar should take over only once the real button has scrolled past.
    this.stickyBarEl = initStickyBar({ bar: this.refs.stickybar, anchor: this.refs.cta || this.querySelector('.dip-media') });
    // Same carousel module as the PDP galleries (@theme/dip-gallery).
    // Arrows here rather than the PDPs' thumbnail rail, so the dots stay on at every width.
    initGallery({
      track: this.refs.gallery, dots: this.refs.dots, thumbs: this.refs.thumbs,
      prev: this.refs.galPrev, next: this.refs.galNext, label: 'image',
    });
    this.render();
  }

  #tier() { return this.tiers[this.tierIdx] || {}; }
  // Copy token: [worth] → the welcome kit's value, formatted in the active currency.
  #withWorth(text, worth) { return String(text || '').replace(/\[worth\]/g, moneyTidy(worth ?? (this.model.gift?.kitWorth || 0))); }

  // What the kit is worth right now: bottle + cloth + the pack actually being gifted, priced at the
  // PLAN price for the chosen cadence (a gift's worth is what the shopper would otherwise pay on
  // subscription). The pack only counts when a code can make it free, so the figure can never
  // advertise value the cart will bill for. Falls back to the site-wide kit worth.
  #kitWorth(L, months) {
    const g = this.model.gift || {};
    const base = (Number(g.bottleValue) || 0) + (Number(g.clothValue) || 0);
    const v = L?.msc;
    const packRaw = v ? (v.plans?.[months]?.raw ?? v.otRaw ?? 0) : 0;
    const packCounts = v && this.#mscCodeFor(L.mscSize) && (L.mscLine || !L.kit);
    const worth = base + (packCounts ? packRaw : 0);
    return worth > 0 ? worth : Number(g.kitWorth) || 0;
  }
  #cadence(m) { return m === 1 ? 'every month' : m === 12 ? 'once a year' : `every ${m} months`; }

  // Publishes BOTH welcome-kit offers to the guard's registry:
  //   1. the free kit with laundry     (priority 10) — laundry sub triggers a free MSC pack sub
  //   2. the paid MSC-starter kit      (priority 1)  — an MSC pack of any purchase type triggers a
  //      free bottle + cloth, but only when there is no qualifying laundry sub (blockedBy), because
  //      that cart is a welcome cart and offer 1 must win it.
  // The SLOT key records every code we own on this surface so a re-render cleans up its own stale
  // entries without touching another page's offers.
  #publishGuardOffer() {
    const OFFERS = 'dip:offers';
    const SLOT = 'dip:offer-slot:home-bundle';
    try {
      let map;
      try { map = JSON.parse(localStorage.getItem(OFFERS) || '{}') || {}; } catch { map = {}; }
      // Drop whatever this surface published last time (single code or CSV list).
      (localStorage.getItem(SLOT) || '').split(',').filter(Boolean).forEach((c) => { delete map[c]; });

      const mine = [];
      const laundryTrigger = (this.model.laundry?.variants || [])
        .filter((v) => v.washes >= this.threshold)
        .map((v) => Number(v.id));
      const bottle = Number(this.model.gift?.bottleVariantId) || null;
      const cloth = Number(this.model.gift?.clothVariantId) || null;
      const hardware = [bottle, cloth].filter(Boolean);

      // 1. free-with-laundry kit — ONE offer per pack size, because each first-pack-free code is a
      // Basic code restricted to its own variant (100% off, subscription-only, once per customer).
      // Only one MSC line is ever in the cart, so only one of these can qualify at a time; priority
      // 10 + packs keeps that deterministic (and hands a mixed cart the largest free pack) while
      // still beating the paid-kit codes at priority 1.
      if (laundryTrigger.length) {
        Object.entries(this.mscCodes || {}).forEach(([size, c]) => {
          if (!c) return;
          const v = (this.model.msc?.variants || []).find((x) => x.size === size && x.plans && Object.keys(x.plans).length);
          if (!v) return;
          map[String(c).toUpperCase()] = {
            code: c,
            trigger: laundryTrigger,
            reward: [Number(v.id)],
            rewardSub: true,
            priority: 10 + (Number(v.packs) || 0),
            // Basic 100% code (zeroes the whole line): the guard may clamp its reward to qty 1.
            // Never set on the BXGY paid-kit codes below (see dip-buy-box.js for the full note).
            clampReward: true,
          };
          mine.push(String(c).toUpperCase());
        });
      }

      // 2. paid MSC-starter kit, one code per pack size
      let kitCodes = {};
      try { kitCodes = JSON.parse(this.dataset.kitCodes || '{}') || {}; } catch { kitCodes = {}; }
      if (hardware.length) {
        (this.model.msc?.variants || []).forEach((v) => {
          const kc = kitCodes[v.size];
          if (!kc) return;
          map[kc.toUpperCase()] = {
            code: kc,
            trigger: [Number(v.id)],
            triggerSub: false,   // the shopper may buy the pack one-time or on subscription
            reward: hardware,
            rewardSub: false,    // bottle + cloth always ride as one-time lines
            blockedBy: laundryTrigger,
            priority: 1,
          };
          mine.push(kc.toUpperCase());
        });
      }

      localStorage.setItem(OFFERS, JSON.stringify(map));
      if (mine.length) localStorage.setItem(SLOT, mine.join(','));
      else localStorage.removeItem(SLOT);
    } catch { /* storage unavailable — guard stays inert */ }
  }

  #bind() {
    this.refs['line-laundry']?.addEventListener('click', () => {
      this.on.laundry = !this.on.laundry;
      if (this.on.laundry) this.kitPick.free = true;
      this.render();
    });
    this.refs['line-dishwasher']?.addEventListener('click', () => { this.on.dishwasher = !this.on.dishwasher; this.render(); });
    const toggleKit = () => {
      const L = this.#lines(true);
      if (!L.kitAvailable) return;
      this.kitPick[L.free ? 'free' : 'paid'] = !this.kitPick[L.free ? 'free' : 'paid'];
      this.render();
    };
    // Two controls, one state: the Multi-Surface product card at the top and the keep/drop row inside
    // the kit dropdown by the summary. render() syncs both every time.
    this.refs.kitMaster?.addEventListener('click', toggleKit);
    // The tick sits inside <summary>, so its click must not reach the disclosure.
    this.refs.kitTick?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); toggleKit(); });
    this.refs.mscRow?.addEventListener('click', () => {
      const L = this.#lines(true);
      if (!L.kit) return;               // kit dropped: put it back first
      if (!L.msc) return;               // no pack at this size
      this.msc = !this.msc;
      this.render();
    });
    this.querySelectorAll('input[name="dip-bundle-tier"]').forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.tierIdx = Number(r.value) || 0;
        this.months = Number(this.#tier().months) || this.months;
        this.freqTouched = false;
        this.render();
      });
    });
    this.refs.fab?.addEventListener('click', () => { this.fab = !this.fab; this.render(); });
    this.querySelectorAll('input[name="dip-bundle-scent"]').forEach((r) => r.addEventListener('change', () => { this.scent = r.value; this.render(); }));
    this.refs.freq?.addEventListener('change', () => { this.months = Number(this.refs.freq.value) || this.months; this.freqTouched = true; this.render(); });
    // Recap per-line +/- (event delegation).
    this.refs.recapList?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-step]');
      if (!btn) return;
      const key = btn.dataset.step; const dir = Number(btn.dataset.dir) || 0;
      this.qty[key] = Math.max(1, Math.min(10, (this.qty[key] || 1) + dir));
      this.render();
    });
    this.refs.cta?.addEventListener('click', (e) => { e.preventDefault(); this.#addToCart(true); });
    // The floating bar's button adds the same basket as the main one.
    this.refs.sbCta?.addEventListener('click', (e) => { e.preventDefault(); this.refs.cta?.click(); });
    this.refs.once?.addEventListener('click', (e) => { e.preventDefault(); this.#addToCart(false); });
  }

  // ---- The kit's MSC pack size -------------------------------------------------
  // The welcome kit is a FIXED gift: each tier authors its own pack size (the household bump) and the
  // section's `msc_free_size` is the floor. The delivery frequency changes WHEN it arrives, never what
  // is in it. Charlie 2026-07-28, on an earlier cadence-derived version: "changing the frequency is
  // now also changing the free welcome kit, this shouldn't happen, it should just be the same but
  // delivered on the cadence they have chosen."
  #mscSizeFor(tier) {
    const list = this.model.msc?.variants || [];
    const packsOf = (size) => Number((list.find((v) => v.size === size) || {}).packs) || 0;
    const tierSize = (tier || {}).mscSize || '';
    const floorSize = this.model.msc?.freeSize || '';
    if (!tierSize) return floorSize;
    return packsOf(floorSize) > packsOf(tierSize) ? floorSize : tierSize;
  }

  #mscCodeFor(size) { return (this.mscCodes || {})[size] || null; }

  #variant(product, size, scent) {
    const list = this.model[product]?.variants || [];
    return list.find((v) => v.size === size && (!scent || v.scent === scent || !v.scent))
      || list.find((v) => v.size === size) || null;
  }

  // The fabric conditioner variant whose washes match a tier's laundry size, using the
  // same byWashes map the laundry PDP resolves against (so both surfaces add the same line).
  #fabVariantFor(tier) {
    const w = parseInt(String((tier || {}).laundrySize || '').replace(/[^0-9]/g, ''), 10) || 0;
    return this.model.fabcon?.byWashes?.[w] || null;
  }
  #fabVariant() { return this.#fabVariantFor(this.#tier()); }

  // What one delivery costs for a given tier + cadence with the shopper's CURRENT selection.
  // Every price on this section runs through here, so the tier cards, the headline, the billed
  // note and the one-time button can never disagree about what is in the bundle.
  //   mode     'sub' = plan price at `months`; 'ot' = one-time price.
  //   linesSub  which basket to price (true keeps the subscription line-up, so the "save vs
  //             one-time" comparison is the same products at one-time prices).
  //   addon     include the fabric conditioner. FALSE for the tier cards: on the laundry PDP a
  //             size card quotes that size's own price and the add-on carries its own £/mo
  //             underneath, so counting it in both places double-reads it.
  //   qty       apply the recap quantities. Also false for the tier cards, which quote the price
  //             of a size (a menu price), not the shopper's running total.
  #cycleRaw(tier, months, { mode = 'sub', linesSub = true, addon = true, qty = true } = {}) {
    const L = this.#linesFor(tier, linesSub, months);
    const price = (v) => (mode === 'sub' ? (v.plans?.[months]?.raw ?? v.otRaw) : v.otRaw);
    const n = (key) => (qty ? this.qty[key] : 1);
    let raw = 0;
    if (L.laundry) raw += price(L.laundry) * n('laundry');
    if (L.dishwasher) raw += price(L.dishwasher) * n('dishwasher');
    const fabV = this.#fabVariantFor(tier);
    if (addon && L.laundry && this.fab && fabV) raw += price(fabV) * n('fabcon');
    if (L.mscLine && !L.mscFree) raw += price(L.msc);
    return raw;
  }

  // ---- Savings ---------------------------------------------------------------
  // Three real, separately-earned savings, added up (what the big refill brands show):
  //   1. subscription  the same basket priced one-time minus its plan price
  //   2. bigger packs  what that volume would cost at the SMALLEST pack's unit rate, one-time both
  //                    sides so it is purely the pack-size effect. Clamped at 0 per line: if a size
  //                    is not actually better value per unit, it must never show as a saving.
  //   3. free gift     the welcome kit's worth, and only when a code can really make the pack free
  // Every input comes from the data island, so all of it moves with the selection and the currency.
  #unitRate(product) {
    const list = this.model[product]?.variants || [];
    const units = (v) => Number(v.washes) || Number(v.packs) || 0;
    // Smallest size that exists = the reference someone would otherwise re-buy.
    const base = list.filter((v) => units(v) > 0 && v.otRaw).sort((a, b) => units(a) - units(b))[0];
    return base ? base.otRaw / units(base) : 0;
  }

  #packSaveFor(product, variant, qty) {
    if (!variant) return 0;
    const units = Number(variant.washes) || Number(variant.packs) || 0;
    const rate = this.#unitRate(product);
    if (!units || !rate) return 0;
    return Math.max(0, units * rate - variant.otRaw) * (qty || 1);
  }

  #savings(tier, months) {
    const L = this.#linesFor(tier, true, months);
    const sub = this.#cycleRaw(tier, months);
    const once = this.#cycleRaw(tier, months, { mode: 'ot' });
    const subSave = Math.max(0, once - sub);

    let packSave = 0;
    if (L.laundry) packSave += this.#packSaveFor('laundry', L.laundry, this.qty.laundry);
    if (L.dishwasher) packSave += this.#packSaveFor('dishwasher', L.dishwasher, this.qty.dishwasher);
    const fabV = this.#fabVariantFor(tier);
    if (L.laundry && this.fab && fabV) packSave += this.#packSaveFor('fabcon', fabV, this.qty.fabcon);
    // The gifted pack's value sits in `gift`, so it must not be counted here as well.
    if (L.mscLine && !L.mscFree) packSave += this.#packSaveFor('msc', L.msc, 1);

    const gift = L.kit && L.free ? this.#kitWorth(L, months) : 0;
    return { subSave, packSave, gift, total: subSave + packSave + gift };
  }

  // ---- Tier bonus chip ------------------------------------------------------
  // What a tier adds over the PRE-SELECTED one: money saved on top, and a bigger welcome kit. Returns
  // false (and writes nothing) for the baseline tier and for anything that does not actually beat it, so
  // the chip can only ever appear where there is a real bonus to claim.
  #paintTierBonus(el, idx, months) {
    const C = this.copy;
    if (idx === this.baseIdx) return false;
    const base = this.tiers[this.baseIdx];
    const tier = this.tiers[idx];
    if (!base || !tier) return false;
    const baseMonths = this.freqTouched ? this.months : (Number(base.months) || this.months);

    const bits = [];
    // Extra saving, expressed PER WASH. Annual spend cannot be compared across these tiers: Stock Up
    // delivers 360 washes once a year where Big Family delivers 120 every two months, so the same money
    // buys wildly different volumes and a "£ saved per year" figure flatters whichever tier ships most
    // often. Cost per wash is the one comparison that holds, and being a ratio it is currency-safe.
    const perWash = (t, mo) => {
      const L2 = this.#linesFor(t, true, mo);
      const washes = (Number(L2.laundry?.washes) || 0) + (Number(L2.dishwasher?.washes) || 0);
      if (!washes) return 0;
      // Menu price for the size: selected products only, no add-on, no quantities (matches the card).
      return this.#cycleRaw(t, mo, { addon: false, qty: false }) / washes;
    };
    const mineRate = perWash(tier, months), baseRate = perWash(base, baseMonths);
    const saveCopy = C.tierBonusSave;
    if (saveCopy && mineRate > 0 && baseRate > 0 && mineRate < baseRate) {
      const pct = Math.round((1 - mineRate / baseRate) * 100);
      if (pct >= 1) bits.push(saveCopy.replace(/\[pct\]/g, `${pct}%`));
    }

    // Bigger welcome kit: only claimable when a first-pack-free code exists for that size, otherwise the
    // "bigger kit" is a pack the cart would bill for.
    const packsOf = (t) => {
      const size = this.#mscSizeFor(t);
      if (!this.#mscCodeFor(size)) return 0;
      const v = (this.model.msc?.variants || []).find((x) => x.size === size);
      return Number(v?.packs) || 0;
    };
    const kitCopy = C.tierBonusKit;
    const mine = packsOf(tier), theirs = packsOf(base);
    if (kitCopy && mine > theirs && theirs > 0) {
      const times = mine / theirs;
      bits.push(kitCopy.replace(/\[packs\]/g, Number.isInteger(times) ? String(times) : times.toFixed(1)));
    }

    if (!bits.length) return false;
    el.textContent = bits.join(' · ');
    return true;
  }

  #hwQty(mscSize) {
    const cfg = this.model.msc?.kitConfig?.[mscSize] || {};
    return { bottle: Number(cfg.bottle) || 1, cloth: Number(cfg.cloth) || 1 };
  }

  #lines(sub) { return this.#linesFor(this.#tier(), sub, this.months); }

  #linesFor(tier, sub, months = this.months) {
    const t = tier || {};
    const out = { laundry: null, dishwasher: null };
    if (this.on.laundry) out.laundry = this.#variant('laundry', t.laundrySize, this.scent);
    if (this.on.dishwasher) out.dishwasher = this.#variant('dishwasher', t.dishwasherSize, this.dwScent);
    const mscSize = this.#mscSizeFor(t);
    const mscV = this.#variant('msc', mscSize);
    out.mscSize = mscSize;
    out.msc = mscV;
    const hardware = !!(this.model.gift?.bottleVariantId || this.model.gift?.clothVariantId);
    // Welcome mode: a qualifying laundry SUBSCRIPTION is in the plan, so the hardware ships free
    // (the FREE-GIFT-LD1 automatic) and the kit is pre-ticked. The threshold matches the guard's
    // trigger list, so the UI can't offer a gift the guard would refuse to unlock.
    const free = !!(sub && out.laundry && Number(out.laundry.washes) >= this.threshold);
    const paidBillable = !!(mscV && (sub ? mscV.plans?.[months] : mscV.otRaw));
    out.free = free;
    out.kitAvailable = hardware && !!mscV && (free || paidBillable);
    out.kit = out.kitAvailable && (free ? this.kitPick.free : (paidBillable && this.kitPick.paid));
    out.mscLine = !!(out.kit && mscV && this.msc);
    // The PACK is only free when a code exists for this exact size: FREE-MSC-LD1 is restricted to the
    // 1-pack variant, so claiming "free" on any other size bills the shopper in full at checkout.
    out.mscFree = out.mscLine && free && !!this.#mscCodeFor(mscSize);
    return out;
  }

  render() {
    const t = this.#tier();
    const m = this.months;
    const C = this.copy;
    const L = this.#lines(true);

    // Tier cards: per-month cost of the CURRENT selection at each tier, so the figures move as
    // products, the add-on, scent and quantities change. They also follow the CHOSEN cadence once
    // the shopper touches the frequency: the price they are comparing is what this size would cost
    // on the delivery interval they just picked, not on the tier's own default (which left every
    // card frozen while the add-on row repriced right next to it).
    this.querySelectorAll('input[name="dip-bundle-tier"]').forEach((r) => {
      const idx = Number(r.value) || 0; const tr = this.tiers[idx] || {};
      const sel = idx === this.tierIdx; r.checked = sel;
      const label = r.closest('.dip-tier'); label?.classList.toggle('is-selected', sel);
      const tm = this.freqTouched ? m : Number(tr.months) || 2;
      // Menu price for this size: selected products only, no add-on, no quantities (PDP parity).
      const raw = this.#cycleRaw(tr, tm, { addon: false, qty: false });
      const permoEl = label?.querySelector('[data-permo]');
      // Empty basket: no figure rather than a misleading zero.
      if (permoEl) permoEl.innerHTML = raw > 0 ? `${perMonth(raw, tm)}<small>/mo</small>` : '';
      // Subline: how much you get, cadence stripped out. The authored summary carries the tier's default
      // cadence ("120 wash each · every 2 months"), which could contradict the figure below it; the
      // cadence now lives in the billed clause, quoted at the interval actually being priced.
      const sumEl = label?.querySelector('[data-tiersum]');
      if (sumEl) {
        const head = String(tr.summary || '').split('·').map((x) => x.trim())
          .filter((p) => p && !/month|year|annual/i.test(p));
        // "60 wash each" only means anything with more than one wash-bearing product in the bundle.
        // With just laundry selected it claimed an "each" that had nothing to be each of. Dropping the
        // word leaves "60 wash", so the count is pluralised back on the way out.
        const washLines = (this.on.laundry ? 1 : 0) + (this.on.dishwasher ? 1 : 0);
        const text = head.join(' · ');
        sumEl.textContent = washLines > 1
          ? text
          : text.replace(/(\d[\d,]*)\s+wash\s+each\b/i, '$1 washes').replace(/\s+each\b/i, '');
      }
      // What this tier charges and how often, same clause and wording as the PDP size cards.
      const billedEl = label?.querySelector('[data-billed]');
      if (billedEl) billedEl.textContent = raw > 0 ? `billed ${money(raw)} ${this.#cadence(tm)}` : '';
      // Per-wash rate on the tiers that claim it. Same cost function as the card price (menu price:
      // selected products, no add-on, no quantities), divided by that tier's washes, so it tracks
      // currency and cadence and can never be typed. Hidden when the tier has no wash-bearing line.
      const pwEl = label?.querySelector('[data-perwash]');
      if (pwEl) {
        const L2 = this.#linesFor(tr, true, tm);
        const washes = (Number(L2.laundry?.washes) || 0) + (Number(L2.dishwasher?.washes) || 0);
        const show = !!tr.showPerWash && washes > 0 && raw > 0;
        pwEl.hidden = !show;
        pwEl.textContent = show ? `Just ${money(raw / washes)}/wash · ${C.perWashNote || 'beats the high street'}` : '';
      }
      // Bonus chip: what this tier adds OVER the pre-selected one. Stock Up saves the most and gifts the
      // biggest welcome kit, and none of that was said anywhere on the card. Both figures are computed, so
      // a change to a tier's size or pack in the editor can never leave the claim behind.
      const bonusEl = label?.querySelector('[data-tierbonus]');
      if (bonusEl) bonusEl.hidden = !this.#paintTierBonus(bonusEl, idx, tm);
    });

    // Product cards.
    const paid = !L.free;
    const kitOn = !!L.kit;
    // The kit's pack, at the size this cadence needs and its plan price for the chosen cadence.
    const mscV = L.msc;
    const mscSubRaw = mscV ? (mscV.plans?.[m]?.raw ?? mscV.otRaw) : 0;
    const setCard = (ref, on) => { const b = this.refs[ref]; if (!b) return; b.classList.toggle('is-selected', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); };
    setCard('line-laundry', this.on.laundry);
    setCard('line-dishwasher', this.on.dishwasher);
    setCard('kitMaster', kitOn);
    // FREE flag: only when Laundry is selected. Toggle display (not [hidden], which
    // loses to .dip-pill's display rule).
    if (this.refs.mscFlag) this.refs.mscFlag.style.display = L.mscFree ? '' : 'none';

    // Laundry scent pills (reveal when Laundry on; theme swatches + selected tick).
    if (this.refs.scentRow) this.refs.scentRow.classList.toggle('is-on', this.on.laundry);
    this.querySelectorAll('input[name="dip-bundle-scent"]').forEach((r) => {
      const label = r.closest('.dip-scent-pill'); if (!label) return;
      const col = this.scentColours[r.value] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
      const on = r.value === this.scent; r.checked = on;
      label.classList.toggle('is-selected', on);
      const sw = label.querySelector('.dip-scent-sw'); if (sw) sw.innerHTML = on ? this.CHECK : '';
    });
    // The laundry card photo follows the chosen formula: the sensitive shot (the Fragrance Free
    // pack, standing in for both sensitive formulas) shows while Fragrance Free or Extra Sensitive
    // is selected. Both imgs are Liquid-rendered; this only flips `hidden`.
    {
      const sensitiveOn = /fragrance free|extra sensitive/i.test(this.scent || '');
      const defImg = this.refs['line-laundry']?.querySelector('img[data-scent-img="default"]');
      const senImg = this.refs['line-laundry']?.querySelector('img[data-scent-img="sensitive"]');
      if (defImg && senImg) {
        defImg.hidden = sensitiveOn;
        senImg.hidden = !sensitiveOn;
      }
    }

    // ---- Offer bar ----
    // Heading names the money, subline names the gifts and the volume, both rewritten from the live
    // selection. When the offer is not in play the bar greys out (PDP `.dip-kit.is-locked` treatment)
    // AND the copy states the prerequisite, so "not included" never has to be inferred.
    const worth = this.#kitWorth(L, m);
    const bottles = Number(mscV?.bottles) || 0;
    const bottleWord = bottles > 0
      ? `${bottles} × ${C.bottleSize || '300ml'} ${C.bottleWord || 'bottles of surface cleaner'}`
      : (C.bottleWord || 'bottles of surface cleaner');
    const fill = (text) => this.#withWorth(String(text || '')
      .replace(/\[bottles\]/g, bottleWord)
      .replace(/\[washes\]/g, String(this.threshold))
      .replace(/\[pack\]/g, moneyTidy(mscSubRaw)), worth);
    if (this.refs.kitStrip) {
      this.refs.kitStrip.classList.toggle('is-on', L.free && kitOn);
      this.refs.kitStrip.classList.toggle('is-off', L.free && !kitOn);
      this.refs.kitStrip.classList.toggle('is-locked', !L.free);
    }
    if (this.refs.kitBarH) {
      this.refs.kitBarH.textContent = fill((L.free && kitOn)
        ? (C.offerHeadingOn || 'Free [worth] welcome kit, included with Laundry')
        : L.free ? (C.offerHeading || 'Free [worth] welcome kit')
          : (C.offerHeadingOff || 'Free [worth] welcome kit with Laundry Sheets'));
    }
    if (this.refs.kitBarS) {
      let sub;
      if (L.free && kitOn) {
        if (L.mscFree) sub = C.offerSubOn || 'Spray bottle, cloth and [bottles], free while Laundry Sheets are in your plan';
        // Sheets dropped on purpose: the hardware is still a free gift, so say that rather than
        // implying the pack is an unpaid extra.
        else if (!L.mscLine) sub = C.offerSubHw || 'Spray bottle and cloth, free while Laundry Sheets are in your plan';
        else sub = C.offerPartial || 'Spray bottle and cloth free. The pack this size needs is [pack].';
      } else if (L.free) sub = C.offerSubOff || 'You removed the kit. Tick it to add it back.';
      else sub = C.offerSub || 'Add Laundry Sheets ([washes]+ washes) on subscription and it is included';
      this.refs.kitBarS.textContent = fill(sub);
    }
    if (this.refs.kitBarFlag) this.refs.kitBarFlag.hidden = !(L.mscFree && kitOn);

    // ---- Welcome kit dropdown (by the summary) ----
    if (this.refs.kitBox) {
      this.refs.kitBox.classList.toggle('is-on', kitOn);
      this.refs.kitBox.classList.toggle('is-off', L.kitAvailable && !kitOn);
      this.refs.kitBox.classList.toggle('is-locked', !L.kitAvailable);
    }
    if (this.refs.kitTick) this.refs.kitTick.setAttribute('aria-pressed', kitOn ? 'true' : 'false');
    if (this.refs.kitTag) this.refs.kitTag.hidden = !(L.free && kitOn);
    // Heading carries the kit's live value (PDP parity: "worth £35"). The worth moves with the pack size
    // the tier gifts and with the shopper's currency, so it is computed here and never authored.
    if (this.refs.kitBoxH) {
      const h = this.refs.kitBoxH;
      // Liquid rendered the authored title; keep it so re-renders never compound "· worth £35".
      if (h.dataset.title == null) h.dataset.title = h.textContent.trim();
      h.textContent = worth > 0 ? `${h.dataset.title} · ${C.kitWorthWord || 'worth'} ${moneyTidy(worth)}` : h.dataset.title;
    }
    if (this.refs.kitBoxState) {
      // When the kit IS free, lead with WHY it is free. The condition was previously only visible while
      // the kit was locked, so it disappeared exactly when someone might remove the thing earning it.
      const freeWith = C.kitFreeWith || 'Free with your Laundry Sheets subscription';
      this.refs.kitBoxState.textContent = !L.kitAvailable
        ? fill(C.kitLockedHint || 'Add Laundry Sheets ([washes]+ washes) on subscription to unlock')
        : !kitOn
          ? (C.kitDropped || 'Not included')
          : L.mscFree
            ? `${freeWith} · ${C.makesWord || 'makes'} ${bottleWord}`
            : L.free
              ? freeWith
              : (C.kitIn || 'In your routine');
    }
    if (this.refs.kitBottleWas) this.refs.kitBottleWas.textContent = moneyTidy(this.model.gift?.bottleValue || 0);
    if (this.refs.kitClothWas) this.refs.kitClothWas.textContent = moneyTidy(this.model.gift?.clothValue || 0);
    // The sheet subscription row: its own toggle, its own state line.
    if (this.refs.mscRow) {
      const canDrop = kitOn && !!L.msc;
      this.refs.mscRow.classList.toggle('is-selected', L.mscLine);
      this.refs.mscRow.setAttribute('aria-pressed', L.mscLine ? 'true' : 'false');
      this.refs.mscRow.classList.toggle('is-locked', !canDrop);
      this.refs.mscRow.setAttribute('aria-disabled', canDrop ? 'false' : 'true');
    }
    if (this.refs.mscState) this.refs.mscState.textContent = L.mscLine ? '' : (C.mscDropped || 'Removed, tap to add back');
    if (this.refs.kitMscT) {
      const packs = Number(mscV?.packs) || 1;
      this.refs.kitMscT.textContent = `${packs > 1 ? `${packs}× ` : ''}${C.kitMsc || 'Multi-Purpose Sheet Refills'}`;
    }
    if (this.refs.kitMscS) {
      const makes = `${C.makesWord || 'makes'} ${bottleWord}`;
      this.refs.kitMscS.textContent = L.mscFree
        ? `${makes} · ${C.kitMscFreeSub || 'first delivery free, then'} ${moneyTidy(mscSubRaw)} ${this.#cadence(m)}`
        : `${makes} · ${moneyTidy(mscSubRaw)} ${this.#cadence(m)}`;
    }
    if (this.refs.kitMscFree) {
      this.refs.kitMscFree.innerHTML = L.mscFree
        ? `<s>${moneyTidy(mscSubRaw)}</s> ${C.kitFirstFree || '1st FREE'}`
        : moneyTidy(mscSubRaw);
    }

    if (this.refs.freq) this.refs.freq.value = String(m);

    // Fabric conditioner add-on: only offered while Laundry is in the bundle, washes-matched
    // to the tier, and it rides the laundry quantity (identical to the laundry PDP row).
    const fabV = this.#fabVariant();
    const fabOn = this.on.laundry && this.fab && !!fabV;
    const fabSubRaw = fabV ? (fabV.plans?.[m]?.raw ?? fabV.otRaw) : 0;
    const fabOtRaw = fabV ? fabV.otRaw : 0;
    if (this.refs.fabWrap) this.refs.fabWrap.classList.toggle('is-on', this.on.laundry && !!fabV);
    if (this.refs.fab) {
      this.refs.fab.classList.toggle('is-selected', fabOn);
      this.refs.fab.setAttribute('aria-pressed', fabOn ? 'true' : 'false');
    }
    if (this.refs.fabPermo && fabV) this.refs.fabPermo.innerHTML = `${perMonth(fabSubRaw, m)}<small>/mo</small>`;
    // What the add-on actually charges, on the same cadence as the bundle it rides.
    if (this.refs.fabBilled) this.refs.fabBilled.textContent = fabV ? `billed ${money(fabSubRaw)} ${this.#cadence(m)}` : '';

    // Totals: same basket priced two ways (plan price now, one-time for the "save vs" line).
    const subRaw = this.#cycleRaw(t, m);
    const onceRaw = this.#cycleRaw(t, m, { mode: 'ot' });

    // In your plan recap. Each row is title over variant on the left with the stepper aligned to the
    // pair, so the detail reads with the product name instead of floating on the far right.
    if (this.refs.recapList) {
      const step = (key) => `<span class="dip-recap-qty"><button type="button" class="dip-recap-step" data-step="${key}" data-dir="-1" aria-label="Fewer">&minus;</button><span class="dip-recap-qtyn">${this.qty[key]}</span><button type="button" class="dip-recap-step" data-step="${key}" data-dir="1" aria-label="More">+</button></span>`;
      const row = (title, detail, stepper, tag = '') => `<li><span class="dip-recap-tx"><span class="dip-recap-t">${this.CHECK}${title}${tag}</span>${detail ? `<span class="dip-recap-s">${detail}</span>` : ''}</span>${stepper}</li>`;
      const rows = [];
      if (L.laundry) rows.push(row(C.recapMain || 'Laundry Detergent Sheets', `${t.laundrySize}${this.scent ? ` · ${this.scent}` : ''}`, step('laundry')));
      if (fabOn) rows.push(row(C.recapFabcon || 'Fabric Conditioner Sheets', `${fabV.size || t.laundrySize} · ${C.fabconSub || 'same delivery'}`, step('fabcon')));
      if (L.dishwasher) rows.push(row(C.recapDish || 'Dishwasher Sheets', t.dishwasherSize, step('dishwasher')));
      if (L.kit) {
        const bits = ['bottle', 'cloth'];
        const packs = Number(mscV?.packs) || 1;
        const packWord = `${packs > 1 ? `${packs}× ` : ''}multi-surface pack${packs > 1 ? 's' : ''}`;
        bits.push(L.mscFree ? `first ${packWord}` : packWord);
        const tag = L.mscFree ? ' <span class="dip-recap-free-tag">FREE</span>' : '';
        rows.push(row(C.recapKit || 'Welcome kit', bits.join(', '), '', tag));
      }
      this.refs.recapList.innerHTML = rows.join('');
    }

    // Savings stack: the three savings the shopper actually earns, then the total.
    const sv = this.#savings(t, m);
    if (this.refs.savings) {
      this.refs.savings.hidden = sv.total <= 0;
      const set = (rowRef, valRef, amount, fmt = money) => {
        const r = this.refs[rowRef]; const v = this.refs[valRef];
        if (r) r.hidden = amount <= 0;
        if (v) v.textContent = fmt(amount);
      };
      // Every row is a subtraction, so it reads as money coming off rather than as another price.
      const less = (v) => `\u2212${money(v)}`;
      if (this.refs.svSub) this.refs.svSub.textContent = less(sv.subSave);
      set('svPackRow', 'svPack', sv.packSave, less);
      set('svGiftRow', 'svGift', sv.gift, less);
      if (this.refs.svTotal) this.refs.svTotal.textContent = money(sv.total);
      // Contents value against what is charged. A VALUE claim (what the box is worth), never a "was"
      // price: the gift's worth and the pack-size comparison are not prices this basket was sold at.
      if (this.refs.svValue) {
        const tmpl = C.saveValueNote || '[value] of products, yours for [price]';
        this.refs.svValue.hidden = sv.total <= 0 || subRaw <= 0;
        this.refs.svValue.textContent = tmpl
          .replace(/\[value\]/g, moneyTidy(subRaw + sv.total))
          .replace(/\[price\]/g, money(subRaw));
      }
    }

    // Price block. The bold per-month figure and the plain billed clause are two spans on ONE line, so
    // `headline` writes only the number and `billed` only its explanation (see .dip-total-v).
    const cad = this.#cadence(m);
    if (this.refs.headline) {
      const bold = this.refs.headline.querySelector('b');
      const html = subRaw > 0 ? `${perMonth(subRaw, m)}<small>/mo</small>` : money(0);
      if (bold) bold.innerHTML = html; else this.refs.headline.innerHTML = html;
    }
    // Same basket bought one-time, at the same cadence, so the two figures compare directly.
    if (this.refs.was) this.refs.was.textContent = onceRaw > subRaw ? perMonth(onceRaw, m) : '';
    if (this.refs.billed) this.refs.billed.textContent = subRaw > 0 ? `billed ${money(subRaw)} ${cad}` : '';
    // The chip in the price block states the SAME total as the savings stack above it: two pink chips a
    // line apart with different figures reads as a contradiction. The breakdown lives in the card, and
    // the one-time comparison is on the button below.
    if (this.refs.save) {
      this.refs.save.textContent = sv.total > 0 ? `${C.saveWord || 'Save'} ${money(sv.total)} ${C.saveWhen || 'today'}` : '';
      this.refs.save.hidden = sv.total <= 0;
    }
    if (this.refs.mscNote) {
      if (L.mscFree) { this.refs.mscNote.hidden = false; this.refs.mscNote.textContent = `+ multi-surface refills free today, then ${moneyTidy(mscSubRaw)} ${this.#cadence(m)}`; }
      else this.refs.mscNote.hidden = true;
    }

    // Future gifts lock.
    if (this.refs.future) this.refs.future.classList.toggle('is-locked', !L.free);
    if (this.refs.futureLock) { this.refs.futureLock.hidden = L.free; this.refs.futureLock.textContent = L.free ? '' : (C.kitLockedHint || ''); }

    // One-time button: the one-time basket (no free kit) at one-time prices.
    let onceTotal = 0;
    if (this.refs.once) {
      onceTotal = this.#cycleRaw(t, m, { mode: 'ot', linesSub: false });
      this.refs.once.textContent = `${C.onceTitle || 'One-time purchase'} · ${money(onceTotal)}`;
      this.refs.once.hidden = onceTotal <= 0;
    }
    // The kit is a subscriber gift, so say so beside the one-time button rather than in the cart. Only
    // while a kit is genuinely on the table: with no laundry there is nothing to lose by buying once.
    if (this.refs.onceNote) this.refs.onceNote.hidden = !(L.free && kitOn) || onceTotal <= 0;

    // CTA.
    const nothing = !L.laundry && !L.dishwasher && !L.kit;
    if (this.refs.cta) {
      this.refs.cta.disabled = nothing;
      const label = this.refs.ctaLabel || this.refs.cta;
      // States the real charge, then the WHOLE saving that comes with it. It used to strike the
      // one-time price, which quoted the smallest of the three savings and made the offer look worse
      // on the button than in the summary right above it (Charlie 2026-07-31). Stated as a saving,
      // not as a struck price, because only part of the total is a former price for these goods.
      if (!this.adding) {
        label.innerHTML = nothing
          ? 'Choose at least one product'
          : `${C.ctaWord || 'Add to basket'} · ${money(subRaw)}${sv.total > 0 ? ` <span class="dip-cta-save">${C.saveWord || 'Save'} ${money(sv.total)}</span>` : ''}`;
      }
    }

    // --- Floating bar (bundle page only; the homepage renders no bar) ---
    // No struck price in the floating bar: see the note in dip-buy-box.js render().
    if (this.refs.sbPrice) this.refs.sbPrice.textContent = `${perMonth(subRaw, m)}/mo`;
    if (this.refs.sbBilled) this.refs.sbBilled.textContent = subRaw > 0 ? `Billed ${money(subRaw)} ${cad}` : '';
    if (this.refs.sbKit) this.refs.sbKit.style.display = (L.kit && L.free) ? 'inline' : 'none';
    // Saving sits under the button, not inside it: see the note in dip-buy-box.js render().
    if (this.refs.sbCta) this.refs.sbCta.textContent = C.ctaWord || 'Add to basket';
    if (this.refs.sbSave) {
      const showSave = sv.total > 0;
      this.refs.sbSave.textContent = showSave ? `${C.saveWord || 'Save'} ${money(sv.total)}` : '';
      this.refs.sbSave.hidden = !showSave;
    }

    this.#announce(subRaw > 0 ? `Bundle ${perMonth(subRaw, m)} per month, billed ${cad}` : 'Bundle empty');
  }

  // ---- Welcome-kit FLIP: summary mini-thumbs fly into their body rows on expand ----
  // Same pairing as dip-buy-box.js: minis pair 1:1 with the body icon wells, and each well animates
  // FROM its mini's position and scale to rest. Minis hide via CSS when open. Reduced motion: plain
  // expand, no animation.
  #initKitFlip() {
    const kit = this.refs.kitBox, minis = this.refs.kitMinis;
    if (!kit || !minis || kit.tagName !== 'DETAILS' || !kit.animate) return;
    kit.addEventListener('toggle', () => {
      if (!kit.open || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const miniEls = [...minis.querySelectorAll('.dip-kit-mini')];
      const bodyIcs = [...kit.querySelectorAll('.dip-kit-body .dip-kit-ic')];
      miniEls.forEach((mini, i) => {
        // Pair on the product key, not position: a row added or moved would otherwise send one
        // product's thumb flying into another's well. Position is the fallback for older markup.
        const key = mini.dataset.flipKey;
        const target = (key && bodyIcs.find((el) => el.dataset.flipKey === key)) || bodyIcs[i];
        if (!target) return;
        const a = mini.getBoundingClientRect(), b = target.getBoundingClientRect();
        if (!a.width || !b.width) return;
        target.animate(
          [
            { transform: `translate(${a.left - b.left}px, ${a.top - b.top}px) scale(${a.width / b.width})` },
            { transform: 'none' },
          ],
          { duration: 380, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', delay: i * 40, fill: 'backwards' }
        );
      });
    });
  }

  // ---- Order-summary modal ("See full breakdown") ----------------------------
  // The PDP's breakdown, restructured for the bundle: itemised lines, the welcome kit in its own tinted
  // block, then billed-today over what renews. Deliberately no future-gifts ladder (Charlie 2026-07-29):
  // those rewards are not fulfilled yet, and a breakdown is the one screen a shopper opens to check what
  // they will actually be charged. Rebuilt on every open, so it can never quote a stale selection.
  #initSummary() {
    const modal = this.refs.sumModal;
    // TWO openers carry ref="sumOpen" (the recap's "See full breakdown" AND the sticky bar's
    // "what's included"), and the refs map keeps only one of them, which left the sticky bar's
    // button dead (Charlie's store review 3, item 4). Bind the deduped trigger set instead,
    // exactly like dip-buy-box.js does for the same modal on the PDPs.
    const triggers = [...new Set([this.refs.sumOpen, ...this.querySelectorAll('[data-sum-open]')].filter(Boolean))];
    if (!modal || !triggers.length) { triggers.forEach((b) => { b.hidden = true; }); return; }
    const supportsModal = typeof modal.showModal === 'function';
    const open = () => { this.#fillSummary(); supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
    const close = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
    triggers.forEach((b) => b.addEventListener('click', open));
    this.refs.sumModalClose?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    // Close before adding: the cart drawer opens on CartAddEvent and the dialog's top layer would sit
    // over it. Any add error still surfaces on the form below.
    this.refs.sumAdd?.addEventListener('click', () => { close(); this.#addToCart(true); });
  }

  #fillSummary() {
    const el = this.refs.sumBody;
    if (!el) return;
    const C = this.copy, m = this.months, t = this.#tier();
    const L = this.#lines(true);
    const cad = this.#cadence(m);
    const price = (v) => (v ? (v.plans?.[m]?.raw ?? v.otRaw ?? 0) : 0);
    const line = (title, detail, amount) =>
      `<li><span class="dip-sum-t">${title}${detail ? `<small>${detail}</small>` : ''}</span><b>${amount}</b></li>`;

    const lines = [];
    if (L.laundry) {
      lines.push(line(C.recapMain || 'Laundry Detergent Sheets',
        `${t.laundrySize}${this.scent ? ` · ${this.scent}` : ''}${this.qty.laundry > 1 ? ` · ×${this.qty.laundry}` : ''}`,
        money(price(L.laundry) * this.qty.laundry)));
    }
    const fabV = this.#fabVariant();
    if (L.laundry && this.fab && fabV) {
      lines.push(line(C.recapFabcon || 'Fabric Conditioner Sheets',
        `${fabV.size || t.laundrySize} · ${C.fabconSub || 'same delivery'}${this.qty.fabcon > 1 ? ` · ×${this.qty.fabcon}` : ''}`,
        money(price(fabV) * this.qty.fabcon)));
    }
    if (L.dishwasher) {
      lines.push(line(C.recapDish || 'Dishwasher Sheets',
        `${t.dishwasherSize}${this.qty.dishwasher > 1 ? ` · ×${this.qty.dishwasher}` : ''}`,
        money(price(L.dishwasher) * this.qty.dishwasher)));
    }
    let html = lines.length ? `<ul class="dip-sum-lines">${lines.join('')}</ul>` : '';

    if (L.kit) {
      const g = this.model.gift || {};
      const hq = this.#hwQty(L.mscSize);
      const mscRaw = price(L.msc);
      const bottles = Number(L.msc?.bottles) || 0;
      const makes = bottles > 0
        ? `${C.makesWord || 'makes'} ${bottles} × ${C.bottleSize || '300ml'} ${C.bottleWord || 'bottles of surface cleaner'}`
        : '';
      const kit = [];
      const free = (was) => `<b class="dip-sum-free"><s>${moneyTidy(was)}</s> FREE</b>`;
      if (g.bottleVariantId && hq.bottle) {
        kit.push(`<li><span class="dip-sum-t">${hq.bottle > 1 ? hq.bottle + '× ' : ''}${C.kitBottle || 'Refillable spray bottle'}</span>${free(g.bottleValue)}</li>`);
      }
      if (g.clothVariantId && hq.cloth) {
        kit.push(`<li><span class="dip-sum-t">${hq.cloth > 1 ? hq.cloth + '× ' : ''}${C.kitCloth || 'Dip Microfibre Cloth'}</span>${free(g.clothValue)}</li>`);
      }
      if (L.mscLine) {
        const packs = Number(L.msc?.packs) || 1;
        const title = `${packs > 1 ? `${packs}× ` : ''}${C.kitMsc || 'Multi-Purpose Sheet Refills'}`;
        // A pack with no first-pack-free code for its size is BILLED. Never dress it as a gift here.
        const detail = L.mscFree
          ? [makes, `${C.kitMscFreeSub || 'first delivery free, then'} ${moneyTidy(mscRaw)} ${cad}`].filter(Boolean).join(' · ')
          : [makes, `${moneyTidy(mscRaw)} ${cad}`].filter(Boolean).join(' · ');
        const amount = L.mscFree
          ? `<b class="dip-sum-free"><s>${moneyTidy(mscRaw)}</s> ${C.kitFirstFree || '1st FREE'}</b>`
          : `<b>${money(mscRaw)}</b>`;
        kit.push(`<li><span class="dip-sum-t">${title}${detail ? `<small>${detail}</small>` : ''}</span>${amount}</li>`);
      }
      if (kit.length) {
        const kitName = C.recapKit || 'Welcome kit';
        const head = L.free ? `${kitName} · ${(C.kitFreeWith || 'Free with your Laundry Sheets subscription').toLowerCase()}` : kitName;
        html += `<div class="dip-sum-kit"><p class="dip-sum-kit-h">${head}</p><ul class="dip-sum-lines">${kit.join('')}</ul></div>`;
      }
    }

    const subRaw = this.#cycleRaw(t, m);
    let renew = `Then <b>${money(subRaw)}</b> ${cad}`;
    if (L.mscFree) renew += `, plus the multi-surface refills ${moneyTidy(price(L.msc))} ${cad} after the first pack`;
    html += `<div class="dip-sum-total"><div class="dip-sum-today"><span>Billed today</span><b>${money(subRaw)}</b></div><p class="dip-sum-renew">${renew}.</p></div>`;
    html += `<p class="dip-sum-note">Pause, skip or cancel anytime, no minimum term.</p>`;
    el.innerHTML = html;
  }

  #initHowLink() {
    const link = this.refs.howLink, modal = this.refs.subModal;
    if (!link) return;
    if (!modal) { link.hidden = true; return; }
    const supportsModal = typeof modal.showModal === 'function';
    const open = () => { supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
    const close = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
    link.addEventListener('click', open);
    this.refs.subModalClose?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  }

  #lineItems(sub) {
    const m = this.months;
    const L = this.#lines(sub);
    const items = [];
    if (L.laundry) {
      const line = { id: Number(L.laundry.id), quantity: this.qty.laundry };
      if (sub && L.laundry.plans?.[m]) line.selling_plan = Number(L.laundry.plans[m].id);
      items.push(line);
      // Fabric conditioner rides the laundry line: same cadence, same parcel.
      const fabV = this.fab ? this.#fabVariant() : null;
      if (fabV) {
        const fabLine = { id: Number(fabV.id), quantity: this.qty.fabcon };
        if (sub && fabV.plans?.[m]) fabLine.selling_plan = Number(fabV.plans[m].id);
        items.push(fabLine);
      }
    }
    if (L.dishwasher) {
      const line = { id: Number(L.dishwasher.id), quantity: this.qty.dishwasher };
      if (sub && L.dishwasher.plans?.[m]) line.selling_plan = Number(L.dishwasher.plans[m].id);
      items.push(line);
    }
    if (L.kit) {
      const hq = this.#hwQty(L.mscSize);
      const g = this.model.gift || {};
      if (g.bottleVariantId && hq.bottle) items.push({ id: Number(g.bottleVariantId), quantity: hq.bottle, _kit: 1 });
      if (g.clothVariantId && hq.cloth) items.push({ id: Number(g.clothVariantId), quantity: hq.cloth, _kit: 1 });
      // The gift pack is a SUBSCRIPTION line whose first cycle is zeroed by FREE-MSC-LD1 (a Basic
      // code, the only kind that can discount a subscription). That free first cycle seeding an MSC
      // subscriber is the point of the offer, so it must not become a one-time gift.
      if (L.mscLine) {
        const mscLine = { id: Number(L.msc.id), quantity: 1, _kit: 1 };
        if (sub && L.msc.plans?.[m]) mscLine.selling_plan = Number(L.msc.plans[m].id);
        items.push(mscLine);
      }
    }
    return items;
  }

  async #addToCart(sub) {
    const cta = this.refs.cta;
    if (this.refs.error) this.refs.error.hidden = true;
    const items = await dipDropKitDupes(this.#lineItems(sub));
    if (!items.length) return;
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    cta?.setAttribute('aria-busy', 'true');
    this.adding = true;
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Could not add to basket.');
        this.dispatchEvent(new CartErrorEvent('dip-home-bundle', data.message, data.description, data.errors));
        return;
      }
      // One round trip: the count comes from the section HTML we already asked for, so
      // the drawer opens now instead of after a second GET /cart.js (see the helper).
      const itemCount = dipCartCountFromSections(data.sections);
      this.#announce('Bundle added to your basket');
      const label = this.refs.ctaLabel || cta;
      if (label) {
        // innerHTML, not textContent: the label carries a struck was-price.
        const prev = label.innerHTML;
        label.textContent = '✓ Added to basket';
        setTimeout(() => { this.adding = false; label.innerHTML = prev; this.render(); }, 1600);
      }
      this.dispatchEvent(new CartAddEvent(undefined, 'dip-home-bundle', {
        source: 'dip-home-bundle', itemCount: itemCount ?? items.length, sections: data.sections,
      }));
    } catch (err) {
      console.error('[dip-home-bundle] add to cart failed', err);
      this.#showError('Something went wrong. Please try again.');
      this.adding = false;
    } finally {
      cta?.removeAttribute('aria-busy');
    }
  }

  #showError(msg) { if (this.refs.error) { this.refs.error.textContent = msg; this.refs.error.hidden = false; } }
  #announce(msg) { if (this.refs.live) this.refs.live.textContent = msg; }
}

if (!customElements.get('dip-home-bundle')) customElements.define('dip-home-bundle', DipHomeBundle);

// ---- Reveal-on-scroll (same additive entrance as dip-buy-box.js) --------------
(() => {
  if (window.__dipRevealInit) return;
  window.__dipRevealInit = true;
  const reveal = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries, obs) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); obs.unobserve(e.target); } }),
      { rootMargin: '0px 0px -4% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.dip-reveal').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!(r.top < window.innerHeight && r.bottom > 0)) { el.classList.add('dip-pre'); io.observe(el); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal, { once: true });
  else reveal();
})();
